import { useState } from 'react';
import { experience } from '../data/experience';
import type { Job } from '../types';
import './Experience.css';

const INITIAL_VISIBLE = 3;

interface JobCardProps {
  job: Job;
}

/** Derives a 1-letter "logo" mark from a company name. */
function getCompanyMark(company: string): string {
  const cleaned = company.replace(/^(the\s+)/i, '').trim();
  return cleaned.charAt(0).toUpperCase();
}

interface CompanyMarkProps {
  company: string;
  iconSrc?: string;
}

function CompanyMark({ company, iconSrc }: CompanyMarkProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!iconSrc && !imgFailed;

  return (
    <span className={`tl-company-mark ${showImage ? 'tl-company-mark-img' : ''}`} aria-hidden>
      {showImage ? (
        <img
          src={iconSrc}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setImgFailed(true)}
        />
      ) : (
        getCompanyMark(company)
      )}
    </span>
  );
}

function JobCard({ job }: JobCardProps) {
  return (
    <li className={`tl-item ${job.highlight ? 'tl-highlight' : ''}`}>
      <div className="tl-marker">
        <span className="tl-dot" />
        <span className="tl-line" aria-hidden />
      </div>
      <div className="tl-card">
        <div className="tl-top">
          <div className="tl-headline">
            <CompanyMark company={job.company} iconSrc={job.iconSrc} />
            <div className="tl-titles">
              <h3 className="tl-role">
                {job.role}
                {job.highlight ? <span className="tl-current">current</span> : null}
              </h3>
              <p className="tl-company">{job.company}</p>
            </div>
          </div>
          <div className="tl-meta">
            <span className="mono">{job.period}</span>
            {job.meta ? <span>{job.meta}</span> : null}
            {job.location ? <span>{job.location}</span> : null}
          </div>
        </div>
        <p className="tl-summary">{job.summary}</p>
        {job.bullets && job.bullets.length > 0 ? (
          <ul className="tl-bullets">
            {job.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

export function Experience() {
  const [expanded, setExpanded] = useState(false);

  const visibleJobs = expanded ? experience : experience.slice(0, INITIAL_VISIBLE);
  const hiddenCount = experience.length - INITIAL_VISIBLE;
  const canExpand = hiddenCount > 0;

  return (
    <section className="section" id="work" aria-labelledby="work-heading">
      <div className="section-head" data-reveal>
        <span className="section-index">05</span>
        <h2 id="work-heading">
          Work history <span className="section-count">/ {experience.length}</span>
        </h2>
        <p className="section-sub">A decade of Ruby, Rails, and product delivery.</p>
      </div>

      <ol className="timeline">
        {visibleJobs.map((job) => (
          <JobCard key={`${job.company}-${job.period}`} job={job} />
        ))}
      </ol>

      {canExpand ? (
        <div className="tl-more-wrap" data-reveal>
          <button
            type="button"
            className="tl-more"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="work-heading"
          >
            <span>{expanded ? 'Show less' : `Show ${hiddenCount} more`}</span>
            <span className={`tl-more-arrow ${expanded ? 'is-open' : ''}`} aria-hidden>
              ↓
            </span>
          </button>
        </div>
      ) : null}
    </section>
  );
}
